<template>
  <div>
    <slot name="adr-uploader-header" />

    <div class="uploadBlockWrapper">
      <div class="uploadBlock">
        <div class="container">
          <scrollbar class="scrollWrapper">
            <div class="documentsHeader">
              {{
                $t('additionalDataUploader.additionalDocuments.documentsLabel', {
                  count: uploadedFiles.length,
                })
              }}
            </div>
            <div v-if="!uploadedFiles.length" class="noUploads">
              {{ $t('additionalDataUploader.additionalDocuments.noUploads') }}
            </div>
            <div v-if="uploadedFiles.length" class="uploadedFiles">
              <uploaded-file
                v-for="(file, index) in uploadedFiles"
                :file="file"
                :key="index"
                class="file"
                @delete="onDelete"
                @retry="onRetry"
              />
            </div>
          </scrollbar>
        </div>

        <div class="dropZoneWrapper">
          <bank-statement-drop-zone
            class="dropZoneComponent"
            v-for="(adr, index) in filteredAdrs"
            :adr="adr"
            :key="index"
            @picked="onUploadModified"
            @delete="onDelete"
          ></bank-statement-drop-zone>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import BankStatementDropZone from '@/components/BankStatementDropZone';
import UploadedFile from '@/components/UploadedFile';
import adrStatuses from '@/constants/adr-statuses';
import { ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR } from '@/modules/illion-adrs';
import applicationMixin from '@/mixins/application';
import uploadFilesMixin from '@/mixins/upload-files';

export default {
  name: 'adr-uploader',
  components: {
    BankStatementDropZone,
    UploadedFile,
  },
  computed: {
    ...mapGetters({
      allFiles: 'file/getAll',
      filterFilesByQuery: 'file/filterFilesByQuery',
      adrs: 'adrs/getAdrs',
    }),
    filteredAdrs() {
      return this.adrs.filter(
        adr =>
          adr.status === adrStatuses.REQUESTED &&
          ![ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR].includes(adr.template.name),
      );
    },
    uploadedFiles() {
      return this.allFiles.filter(
        file =>
          file.isUploaded &&
          ![ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR].includes(file.adr.template.name),
      );
    },
    uploadedAdrs() {
      return this.filteredAdrs.filter(adr => {
        const uploadedFiles = this.filterFilesByQuery({ adrId: adr.id }).filter(
          file => file.isUploaded,
        );

        return uploadedFiles.length >= adr.numberOfFilesRequired;
      });
    },
    uploadedAdrIds() {
      return this.uploadedAdrs.map(adr => adr.id);
    },
  },
  methods: {
    ...mapActions({
      onUpload: 'file/uploadFile',
      onDelete: 'file/deleteFile',
      onRetry: 'file/retryUpload',
    }),
    onUploadModified(args) {
      this.onUpload(args);
      this.$emit('docUpload');
    },
  },
  mixins: [applicationMixin, uploadFilesMixin],
};
</script>
<style lang="sass" scoped>
.subtitle
  +font-size(16, 24)
  white-space: pre-line

.dropZoneComponent
  margin-bottom: $glass-spacing-2
  padding-right: $glass-spacing-3
  & + .dropZoneComponent
    margin-bottom: 0
  +mq($stego)
    margin-bottom: 0
  ::v-deep
    .dropzone
      margin-bottom: $glass-spacing-3
      +mq($stego)
        height: baseunit(15.2)
        margin-bottom: 0
        &:last-child
          margin-right: 0

    .details
      +mq($stego)
        max-width: baseunit(19.4)

    .errorMsg
      +mq($stego)
        max-width: baseunit(19.4)

.uploadBlockWrapper
  display: grid

.uploadBlock
  width: 100%
  max-width: baseunit(68)
  margin-top: $glass-spacing-1
  margin-bottom: $glass-spacing-3

.dropZoneWrapper
  padding: $glass-spacing-3
  display: flex
  justify-content: space-between
  width: 100%
  flex-direction: column
  margin-top: $glass-spacing-3
  border: 1px solid $glass-color-system-neutral-grey-04
  border-radius: 4px
  background: $glass-color-system-neutral-white
  +mq($stego)
    flex-direction: row
    flex-wrap: wrap
    justify-content: start
    padding-right: 0

.container
  width: 100%
  border: 1px solid $glass-color-system-neutral-grey-04
  border-radius: 4px
  min-height: baseunit(17.6)
  background: $glass-color-system-neutral-white
  padding-top: $glass-spacing-05

  .documentsHeader
    display: flex
    align-items: center
    margin-left: $glass-spacing-3
    height: baseunit(4)
    padding-top: $glass-spacing-105
    font-weight: bold
    position: sticky
    background: $glass-color-system-neutral-white
    top: 0
    +font-size(16, 24)
    z-index: 100

  .noUploads
    margin-left: $glass-spacing-3
    margin-top: $glass-spacing-3
    +font-size(13.33, 20)
    color: $glass-color-system-neutral-grey-08

.uploadedFiles
  max-width: baseunit(42)
  margin-left: $glass-spacing-3

  .file
    margin: $glass-spacing-105 0
    margin-bottom: $glass-spacing-2
    &:last-child
      margin-bottom: 0

    ::v-deep .description
      grid-column-gap: $glass-spacing-105
      grid-template-columns: baseunit(15) 0 baseunit(3.3)
      +mq($stego)
        grid-column-gap: baseunit(5.5)
      +mq($allo)
        grid-template-columns: baseunit(12.8) baseunit(13) baseunit(3.3)

      .progressContainer
        max-width: baseunit(18.5)
        margin-left: $glass-spacing-2

.scrollWrapper
  max-height: baseunit(17.6)
</style>
